import { Fragment, useEffect, useMemo, useRef, useState, type Key, type ReactNode } from "react";
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/Pagination";

// ---------------------------------------------------------------------------
// TableShell — doc/050 §4.3 数据表格交互基线(非列驱动 / 非 tanstack ColumnDef)。
//
// 两类模式:
//   client: 传入全量 data,组件内做 搜索 → 排序 → 分页;total 由组件算出。
//   server: 传入当前页 data 与 total,组件只负责 UI 状态并在变化时通过
//           onParamsChange 冒泡给页面(页面据此 setState → 新 queryKey refetch)。
// 分页/排序/筛选任一变化自动回第 1 页(doc/050 ADR T-D);越界页自动收敛。
// 行渲染走 renderRow 插槽,可返回 <tr> 片段(展开行/多按钮/徽标不迁移)。
// ---------------------------------------------------------------------------

export type ShellSortOrder = "asc" | "desc";

export interface ColumnSpec<T = unknown> {
  /** 排序 key:白名单 key,同时用作 client 模式默认取值字段(可被 sortValue 覆盖)。 */
  key: string;
  label: ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  className?: string;
  /** client 模式排序取值;缺省取 (row as any)[key]。返回 null/undefined 排末尾。 */
  sortValue?: (row: T) => string | number | boolean | null | undefined;
}

export interface ListParams {
  page: number;
  page_size: number;
  sort?: string;
  order?: ShellSortOrder;
  q?: string;
}

export interface TableShellProps<T> {
  mode: "client" | "server";
  columns: ColumnSpec<T>[];
  data?: T[];
  total?: number;
  loading?: boolean;
  emptyText?: string;
  /** 可选全局搜索(防抖 250ms)。client 模式需 match 提取可搜索文本,缺省 JSON 全文。 */
  search?: { placeholder?: string; match?: (row: T) => string };
  /** 渲染在全局搜索框左侧的附加控件(如独立的 ID 精确搜索框)。 */
  searchPrefix?: ReactNode;
  filters?: ReactNode;
  /** 变化时回到第 1 页,但不重挂载组件(保留搜索输入与焦点)。 */
  pageResetKey?: string | number;
  onParamsChange?: (p: ListParams) => void;
  renderRow: (row: T, index: number) => ReactNode;
  rowKey: (row: T) => Key;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  className?: string;
}

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function toComparable(v: unknown): string | number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? v : t;
  }
  return String(v);
}

export function TableShell<T>({
  mode,
  columns,
  data,
  total,
  loading,
  emptyText = "暂无数据",
  search,
  searchPrefix,
  filters,
  pageResetKey,
  onParamsChange,
  renderRow,
  rowKey,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className,
}: TableShellProps<T>) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<ShellSortOrder | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const emitRef = useRef(onParamsChange);
  emitRef.current = onParamsChange;

  // 搜索防抖 250ms —— 只更新 query,输入框保持即时反馈。
  useEffect(() => {
    if (searchInput === query) return;
    const timer = window.setTimeout(() => {
      setQuery(searchInput);
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchInput, query]);

  // 外部筛选(pageResetKey)变化时回第 1 页,但不重挂载 —— 避免搜索框失焦/清空。
  useEffect(() => {
    setPage(1);
  }, [pageResetKey]);

  // server 模式:params 变化即上报,页面据此 refetch。挂载时也上报一次,使外部
  // 通过 key 重挂载(筛选变化)时,页面 params 能同步回第 1 页。
  useEffect(() => {
    if (mode !== "server") return;
    emitRef.current?.({ page, page_size: pageSize, sort, order, q: query || undefined });
  }, [mode, page, pageSize, sort, order, query]);

  // client 模式:搜索 → 排序 → 分页。
  const processed = useMemo(() => {
    if (mode !== "client") return data ?? [];
    let rows = data ?? [];
    const kw = query.trim().toLowerCase();
    if (kw) {
      const match = search?.match ?? ((row: T) => JSON.stringify(row));
      rows = rows.filter((row) => match(row).toLowerCase().includes(kw));
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort);
      if (col) {
        const valueOf: (row: T) => unknown = col.sortValue
          ? (row) => col.sortValue!(row)
          : (row) => (row as Record<string, unknown>)[sort];
        const dir = order === "desc" ? -1 : 1;
        rows = [...rows].sort((a, b) => {
          const av = toComparable(valueOf(a));
          const bv = toComparable(valueOf(b));
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          if (av < bv) return -1 * dir;
          if (av > bv) return 1 * dir;
          return 0;
        });
      }
    }
    return rows;
  }, [mode, data, query, search, sort, order, columns]);

  // server 模式:refetch 期间 data 为 undefined、total 会瞬间塌成 0;若直接用 0 算
  // totalPages 会把 page 误判越界并回第 1 页(冒烟缺陷:点下一页后 2→1、排序后「共 0 条」)。
  // 因此加载中沿用上一次已知 total,只有非 loading 的 total 才可信。
  const lastTotalRef = useRef<number | undefined>(undefined);
  if (mode === "server" && !loading && typeof total === "number") lastTotalRef.current = total;

  const effectiveTotal =
    mode === "client"
      ? processed.length
      : !loading && typeof total === "number"
        ? total
        : lastTotalRef.current ?? total ?? 0;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));

  // 越界自动收敛:数据/页大小变化导致当前页超过总页数时回到末页。
  useEffect(() => {
    if (!loading && (mode === "client" || total !== undefined) && page > totalPages) setPage(totalPages);
  }, [loading, mode, total, page, totalPages]);

  const paged = useMemo(() => {
    if (mode !== "client") return processed;
    const start = (page - 1) * pageSize;
    return processed.slice(start, start + pageSize);
  }, [mode, processed, page, pageSize]);

  const cycleSort = (key: string) => {
    if (sort !== key) {
      setSort(key);
      setOrder("asc");
    } else if (order === "asc") {
      setOrder("desc");
    } else {
      setSort(undefined);
      setOrder(undefined);
    }
    setPage(1);
  };

  const changePage = (next: number) => setPage(Math.min(Math.max(1, next), totalPages));

  return (
    <div className={cn("space-y-3", className)}>
      {(search || searchPrefix || filters) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">{filters}</div>
          {(search || searchPrefix) && (
            <div className="flex w-full items-center gap-2 sm:w-auto">
              {searchPrefix}
              {search && (
                <div className="relative w-full sm:w-64">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={search.placeholder ?? "搜索…"}
                    className="h-9 pl-8 text-sm"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-muted-foreground">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "px-2 py-2 text-xs font-medium first:pl-0 last:pr-0",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center",
                    col.className,
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => cycleSort(col.key)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded transition-colors hover:text-foreground",
                        sort === col.key && "text-foreground",
                      )}
                    >
                      {col.label}
                      {sort !== col.key ? (
                        <ChevronsUpDown className="h-3 w-3 opacity-50" />
                      ) : order === "asc" ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (data?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-8 text-center text-sm text-muted-foreground">
                  加载中…
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-8 text-center text-sm text-muted-foreground">
                  {emptyText}
                </td>
              </tr>
            ) : (
              paged.map((row, index) => <Fragment key={rowKey(row)}>{renderRow(row, index)}</Fragment>)
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        total={mode === "client" ? effectiveTotal : total}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageChange={changePage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />
    </div>
  );
}
